/**
 * How long a tentative claim has left, in words.
 *
 * A hold is only actionable while there is time on it: "lapses in 20 minutes" is what makes a clerk
 * pick up the phone, and an absolute timestamp alone leaves them doing the arithmetic. Both are
 * shown — the clock time is what you say to the other customer, the countdown is what you act on.
 *
 * Pure, and takes `now` as a parameter rather than reading the clock, so the boundaries can be
 * tested without a fake clock — the same discipline `SubscriptionLifecycle.Evaluate` follows on the
 * server.
 */

const minute = 60_000;
const hour = 60 * minute;
const day = 24 * hour;

/**
 * `null` when there is no expiry at all, which is what a confirmed booking looks like — there is
 * nothing to count down and nothing to chase.
 */
export function holdCountdown(
  expiresOn: string | null | undefined,
  now: Date = new Date(),
): string | null {
  if (!expiresOn) {
    return null;
  }

  const expiry = new Date(expiresOn).getTime();

  if (Number.isNaN(expiry)) {
    return null;
  }

  const left = expiry - now.getTime();

  // Past its expiry the row still exists — held and let go is a fact the shop keeps — but it holds
  // nothing, and saying "lapsed" is the difference between chasing a customer and not bothering.
  if (left <= 0) {
    return 'lapsed';
  }

  if (left >= day) {
    const days = Math.floor(left / day);

    return `${days}d left`;
  }

  if (left >= hour) {
    const hours = Math.floor(left / hour);
    const minutes = Math.floor((left % hour) / minute);

    return minutes === 0 ? `${hours}h left` : `${hours}h ${minutes}m left`;
  }

  if (left >= minute) {
    return `${Math.floor(left / minute)}m left`;
  }

  // Rounding this up to "1m" would keep saying a minute is left for a whole minute after it wasn't.
  return 'under a minute left';
}
